// @flow
/* eslint-disable react/no-multi-comp */

import React, { memo } from 'react';

import { isMobileBrowser } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import {
    IconArrowDown,
    IconArrowUp,
    IconChat,
    IconParticipants,
    IconRaisedHand,
    PollIcon
} from '../../../base/icons';
import { connect } from '../../../base/redux';
import { getBrandingColorPalette } from '../../../base/styles';
import { ToolboxButtonWithIcon } from '../../../base/toolbox/components';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { ChatCounter } from '../../../chat';
import SelectLabel from '../../../conference/components/web/SelectLabel';
import TileViewCarousel from '../../../filmstrip/components/web/TileViewCarousel';
import { InviteButton } from '../../../participants-pane/components';
import ReactionsContainerButton from '../../../reactions/components/web/ReactionsContainerButtons';
import ClosedCaptionButton from '../../../subtitles/components/web/ClosedCaptionButton.web';

import AudioSettingsButton from './AudioSettingsButton';
import HangupSettings from './HangupSettings';
import OverflowMenuButton from './OverflowMenuButton';
import OverflowMenuContent from './OverflowMenuContent';
import ScreenOverflowButton from './ScreenOverflowButton';
import ShareMenuContent from './ShareMenuContent';
import ToolbarButton from './ToolbarButton';
import { mapStateToProps } from './Toolbox';
import VideoSettingsButton from './VideoSettingsButton';

/* global interfaceConfig */

type Props = {

    // State props
    _chatOpen: boolean,
    _isPollsTabFocused: boolean,
    _raisedHand: boolean,
    _overflowMenuVisible: boolean,
    _screensharing: boolean,
    _ShareMenuVisibility: boolean,
    _isPictureInPictureEnabled: boolean,
    _visibleButtons: Set<string>,
    _requestingCaptionsModal: boolean,
    _participantsPaneOpen: boolean,
    _isOpenP: boolean,
    _isMobile: boolean,
    _desktopSharingEnabled: boolean,
    _localParticipantID: string,
    _isWhiteBoardID: ?string,
    _isetherPadId: ?string,
    conferenceView: string,
    filmStripView: string,
    participantsLength: number,
    _carouselStartIndex: number,
    totalPages: number,

    // Share menu props (passed to ShareMenuContent)
    _shareVideoState: boolean,
    _whiteBoardvisible: boolean,
    _livePadVisible: boolean,
    _screenShareVisible: boolean,
    _isLocalWhiteboard: boolean,
    _isLocalEtherpad: boolean,
    _isOwnSharingVideo: boolean,
    _isModerator: boolean,
    desktopApp: boolean,
    _desktopSharingDisabledTooltipKey: boolean,

    // Overflow menu props (passed to OverflowMenuContent)
    _clicknPledgeVisible: boolean,
    _donorBoxVisible: boolean,
    _feedbackConfigured: boolean,
    _geturl: string,
    clickAndPledgeUrl: string,
    _isVoiceCommandDisabled: boolean,
    _isGuest: boolean,
    _isVpaasMeeting: boolean,
    _embedMeeting: boolean,
    liveStreamVisible: boolean,
    RecordingVisible: boolean,
    vcVisible: boolean,
    giventToken: ?string,
    _GiventOwnerId: ?string,
    _clickandpledGUID: ?string,
    _conference: Object,
    _isRecordingRunning: boolean,
    _isLiveStreamRunning: boolean,

    // Callbacks
    onToggleRaiseHand: Function,
    onToggleChat: Function,
    onTogglePolls: Function,
    onToggleParticipantsPane: Function,
    onSetOverflowVisible: Function,
    onSetShareScreenOverflowVisible: Function,
    onCaptionsArrowClick: Function,
    onViewStripMenuChange: Function,
    onTotalPagesChange: Function,
    onToggleScreenshare: Function,

    // Overflow callbacks
    onToggleProfile: Function,
    onOpenVideoQuality: Function,
    onOpenSpeakerStats: Function,
    onOpenEmbedMeeting: Function,
    onOpenFeedback: Function,
    onOpenKeyboardShortcuts: Function,
    onToggleDonorbox: Function,
    onRemoveDonorbox: Function,
    onToggleClickAndPledge: Function,
    onRemoveClickAndPledge: Function,
    onToggleVoiceCommands: Function,

    dispatch: Function,
    t: Function,
};

/**
 * Renders footer_conference1 — the primary bottom bar for fixed view.
 * Contains: audio/video controls, screen share, hangup, tile carousel, label selector, invite.
 */
const FooterConference1 = memo(({
    _ShareMenuVisibility,
    _screensharing,
    _visibleButtons,
    _isPictureInPictureEnabled,
    _localParticipantID,
    _isWhiteBoardID,
    _isetherPadId,
    _desktopSharingEnabled,
    _isMobile,
    _carouselStartIndex,
    participantsLength,
    conferenceView,
    filmStripView,
    totalPages,
    onSetShareScreenOverflowVisible,
    onViewStripMenuChange,
    onTotalPagesChange,
    dispatch,
    t,

    // Share menu props
    _shareVideoState,
    _whiteBoardvisible,
    _livePadVisible,
    _screenShareVisible,
    _isLocalWhiteboard,
    _isLocalEtherpad,
    _isOwnSharingVideo,
    _isModerator,
    desktopApp,
    _desktopSharingDisabledTooltipKey,
    onToggleScreenshare
}: Props) => {
    const primaryColor = interfaceConfig?.applyMeetingSettings && getBrandingColorPalette(interfaceConfig)?.primary;
    const secondaryColor = interfaceConfig?.applyMeetingSettings && getBrandingColorPalette(interfaceConfig)?.secondary;

    const footerStyle = {
        background: `${primaryColor}`,
        border: `2px solid ${secondaryColor}`
    };
    const overflowMenuStyle = { background: `${primaryColor}` };

    const shareMenuContent = (
        <ShareMenuContent
            _desktopSharingDisabledTooltipKey = { _desktopSharingDisabledTooltipKey }
            _isLocalEtherpad = { _isLocalEtherpad }
            _isLocalWhiteboard = { _isLocalWhiteboard }
            _isModerator = { _isModerator }
            _isOwnSharingVideo = { _isOwnSharingVideo }
            _isWhiteBoardID = { _isWhiteBoardID }
            _isetherPadId = { _isetherPadId }
            _livePadVisible = { _livePadVisible }
            _localParticipantID = { _localParticipantID }
            _screenShareVisible = { _screenShareVisible }
            _screensharing = { _screensharing }
            _shareVideoState = { _shareVideoState }
            _whiteBoardvisible = { _whiteBoardvisible }
            desktopApp = { desktopApp }
            desktopSharingEnabled = { _desktopSharingEnabled }
            onToggleScreenshare = { onToggleScreenshare } />
    );

    const screenMenuToggled = _screensharing || _isLocalWhiteboard || _isLocalEtherpad || (_shareVideoState && _isOwnSharingVideo);

    return (
        <div
            className = 'footer_conference1'
            style = { footerStyle }>
            <div className = 'group1_fc'>
                <div className = 'group1_fc-settings'>
                    {_visibleButtons.has('microphone') && <div className = 'icon-btn'>
                        <AudioSettingsButton
                            dispatch = { dispatch }
                            key = 'asb'
                            visible = { true } />
                    </div>}
                    {_visibleButtons.has('camera') && <div className = 'icon-btn'>
                        <VideoSettingsButton
                            dispatch = { dispatch }
                            key = 'vsb'
                            visible = { true } />
                    </div>}
                </div>
                {/* Screen share button — only shown on screens wider than 480px via CSS */}
                {!_isPictureInPictureEnabled && _visibleButtons.has('desktop') && (!isMobileBrowser() || _isModerator) && (
                    <div className = 'button screen-share screen-share-wide-only'>
                        <ScreenOverflowButton
                            disabledProps = { React.Children.toArray(shareMenuContent).filter(Boolean).length === 0 }
                            isOpen = { _ShareMenuVisibility }
                            onVisibilityChange = { onSetShareScreenOverflowVisible }
                            toggled = { screenMenuToggled }>
                            <ul
                                aria-label = { t('toolbar.accessibilityLabel.moreActionsMenu') }
                                className = 'overflow-menu'
                                style = { overflowMenuStyle }>
                                {shareMenuContent}
                            </ul>
                        </ScreenOverflowButton>
                    </div>
                )}
                {_visibleButtons.has('hangup') && (
                    <Tooltip
                        content = { t('toolbar.accessibilityLabel.hangup') }
                        position = 'top'>
                        <HangupSettings
                            customClass = 'hangup-button '
                            visible = { _visibleButtons.has('hangup') } />
                    </Tooltip>
                )}
            </div>
            <div className = 'group2_fc'>
                <TileViewCarousel
                    _startIndex = { _carouselStartIndex || 0 }
                    _totalParticipants = { participantsLength }
                    _totalnoofPages = { onTotalPagesChange } />

                {[ 'tileview', 'filmstrip', 'horizontalstrip', 'stageview', 'stage' ].some(btn => _visibleButtons.has(btn)) && (
                    <SelectLabel
                        _filmStripView = { filmStripView }
                        _participantsLength = { participantsLength }
                        _visibleButtons = { _visibleButtons }
                        conferenceView = { conferenceView }
                        dispatch = { dispatch }
                        onViewStripMenuChange = { onViewStripMenuChange }
                        screenSize = { _isMobile }
                        totalPages = { totalPages } />
                )}
                {_visibleButtons.has('invite') && !_isPictureInPictureEnabled && (
                    <InviteButton
                        textToDisplay = { t('addPeople.add') }
                        totalPages = { totalPages } />
                )}
            </div>
        </div>
    );
});

FooterConference1.displayName = 'FooterConference1';

/**
 * Renders footer_conference2 — the secondary bottom bar for fixed view.
 * Contains: captions, raise hand, reactions, chat, polls, participants, overflow menu.
 */
const FooterConference2 = memo(({
    _requestingCaptionsModal,
    _raisedHand,
    _chatOpen,
    _overflowMenuVisible,
    _visibleButtons,
    _isPollsTabFocused,
    _isPictureInPictureEnabled,
    _participantsPaneOpen,
    conferenceView,
    onToggleRaiseHand,
    onToggleChat,
    onTogglePolls,
    onToggleParticipantsPane,
    onSetOverflowVisible,
    onCaptionsArrowClick,
    dispatch,
    t,

    // Overflow props
    _clicknPledgeVisible,
    _donorBoxVisible,
    _feedbackConfigured,
    _screensharing,
    _geturl,
    clickAndPledgeUrl,
    _isVoiceCommandDisabled,
    _isModerator,
    _isGuest,
    _isVpaasMeeting,
    _embedMeeting,
    liveStreamVisible,
    RecordingVisible,
    desktopApp,
    giventToken,
    _GiventOwnerId,
    _clickandpledGUID,
    _localParticipantID,
    _conference,
    _isRecordingRunning,
    _isLiveStreamRunning,
    vcVisible,
    onToggleProfile,
    onOpenVideoQuality,
    onOpenSpeakerStats,
    onOpenEmbedMeeting,
    onOpenFeedback,
    onOpenKeyboardShortcuts,
    onToggleDonorbox,
    onRemoveDonorbox,
    onToggleClickAndPledge,
    onRemoveClickAndPledge,
    onToggleVoiceCommands
}: Props) => {
    const primaryColor = interfaceConfig?.applyMeetingSettings && getBrandingColorPalette(interfaceConfig)?.primary;
    const secondaryColor = interfaceConfig?.applyMeetingSettings && getBrandingColorPalette(interfaceConfig)?.secondary;

    const footerStyle = {
        background: `${primaryColor}`,
        border: `2px solid ${secondaryColor}`
    };
    const overflowMenuStyle = { background: `${primaryColor}` };

    const overflowMenuContent = (
        <OverflowMenuContent
            RecordingVisible = { RecordingVisible }
            _GiventOwnerId = { _GiventOwnerId }
            _clickandpledGUID = { _clickandpledGUID }
            _clicknPledgeVisible = { _clicknPledgeVisible }
            _conference = { _conference }
            _donorBoxVisible = { _donorBoxVisible }
            _embedMeeting = { _embedMeeting }
            _feedbackConfigured = { _feedbackConfigured }
            _geturl = { _geturl }
            _isGuest = { _isGuest }
            _isLiveStreamRunning = { _isLiveStreamRunning }
            _isModerator = { _isModerator }
            _isRecordingRunning = { _isRecordingRunning }
            _isVoiceCommandDisabled = { _isVoiceCommandDisabled }
            _isVpaasMeeting = { _isVpaasMeeting }
            _localParticipantID = { _localParticipantID }
            _screensharing = { _screensharing }
            _visibleButtons = { _visibleButtons }
            clickAndPledgeUrl = { clickAndPledgeUrl }
            desktopApp = { desktopApp }
            dispatch = { dispatch }
            giventToken = { giventToken }
            liveStreamVisible = { liveStreamVisible }
            onOpenEmbedMeeting = { onOpenEmbedMeeting }
            onOpenFeedback = { onOpenFeedback }
            onOpenKeyboardShortcuts = { onOpenKeyboardShortcuts }
            onOpenSpeakerStats = { onOpenSpeakerStats }
            onOpenVideoQuality = { onOpenVideoQuality }
            onRemoveClickAndPledge = { onRemoveClickAndPledge }
            onRemoveDonorbox = { onRemoveDonorbox }
            onToggleClickAndPledge = { onToggleClickAndPledge }
            onToggleDonorbox = { onToggleDonorbox }
            onToggleProfile = { onToggleProfile }
            onToggleVoiceCommands = { onToggleVoiceCommands }
            vcVisible = { vcVisible } />
    );


    return (
        <div
            className = 'footer_conference2'
            style = { footerStyle }>
            {_visibleButtons.has('closedcaptions') && (
                <div className = 'icon-btn'>
                    <Tooltip
                        content = { t('transcribing.OpenCloseCaptions') }
                        position = 'top'>
                        {_requestingCaptionsModal
                            ? <ClosedCaptionButton />
                            : (
                                <ToolboxButtonWithIcon
                                    icon = { _requestingCaptionsModal ? IconArrowDown : IconArrowUp }
                                    iconDisabled = { false }
                                    iconTooltip = { t('transcribing.OpenCloseCaptions') }
                                    isFloatView = { conferenceView === 'fixed' }
                                    onIconClick = { onCaptionsArrowClick }>
                                    <ClosedCaptionButton />
                                </ToolboxButtonWithIcon>
                            )}
                    </Tooltip>
                </div>
            )}
            {_visibleButtons.has('raisehand') && (
                <div className = 'icon-btn'>
                    <ToolbarButton
                        accessibilityLabel = { t('toolbar.accessibilityLabel.raiseHand') }
                        icon = { IconRaisedHand }
                        onClick = { onToggleRaiseHand }
                        position = 'top'
                        toggled = { _raisedHand }
                        tooltip = { t('toolbar.raiseHand') } />
                </div>
            )}
            {_visibleButtons.has('reactions') && (
                <div className = 'icon-btn'>
                    <ReactionsContainerButton onClick = { onToggleRaiseHand } />
                </div>
            )}
            {_visibleButtons.has('chat') && (
                <div className = 'icon-btn'>
                    <div className = 'toolbar-button-with-badge'>
                        <ToolbarButton
                            accessibilityLabel = { t('toolbar.accessibilityLabel.chat') }
                            icon = { IconChat }
                            onClick = { onToggleChat }
                            position = 'top'
                            toggled = { _chatOpen && !_isPollsTabFocused }
                            tooltip = { t('toolbar.chat') } />
                        <ChatCounter />
                    </div>
                </div>
            )}
            {_visibleButtons.has('polls') && (
                <div className = 'icon-btn'>
                    <ToolbarButton
                        accessibilityLabel = { t('toolbar.pollsPanel') }
                        icon = { PollIcon }
                        onClick = { onTogglePolls }
                        position = 'top'
                        toggled = { _chatOpen && _isPollsTabFocused }
                        tooltip = { t('toolbar.pollsPanelTooltip') } />
                </div>
            )}
            {_visibleButtons.has('participants-pane') && (
                <div className = 'icon-btn'>
                    <ToolbarButton
                        accessibilityLabel = { t('toolbar.accessibilityLabel.participants') }
                        icon = { IconParticipants }
                        key = 'participants-pane'
                        onClick = { onToggleParticipantsPane }
                        position = 'top'
                        toggled = { _participantsPaneOpen }
                        tooltip = { t('toolbar.participants') } />
                </div>
            )}
            <div className = 'icon-btn'>
                {!_isPictureInPictureEnabled && (
                    <OverflowMenuButton
                        isOpen = { _overflowMenuVisible }
                        onVisibilityChange = { onSetOverflowVisible }
                        position = 'top'>
                        <ul
                            aria-label = { t('toolbar.accessibilityLabel.moreActionsMenu') }
                            className = 'overflow-menu'
                            style = { overflowMenuStyle }>
                            <div className = 'rows'>{overflowMenuContent}</div>
                        </ul>
                    </OverflowMenuButton>
                )}
            </div>
        </div>
    );
});

FooterConference2.displayName = 'FooterConference2';

/**
 * Combined FixedViewFooter — renders footer_conference1 and footer_conference2
 * for the fixed layout view (desktop, non-tablet).
 */
const FixedViewFooter = memo((props: Props) => {
    const { _isPictureInPictureEnabled, _visibleButtons } = props;

    return (
        <div className = 'footer_conference'>
            {[ 'hangup', 'chat', 'polls', 'microphone', 'camera', 'desktop', 'invite',
                'horizontalstrip', 'tileview', 'filmstrip', 'stageview', 'stage' ].some(btn => _visibleButtons.has(btn))
                && <FooterConference1 { ...props } />}
            {!_isPictureInPictureEnabled && <FooterConference2 { ...props } />}
        </div>
    );
});

FixedViewFooter.displayName = 'FixedViewFooter';

export default translate(connect(mapStateToProps)(FixedViewFooter));
